<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'
import {
  $getChangeRequestSubtask,
  $getChangeRequestSubtaskOptions,
  $updateChangeRequestSubtask
} from '@/api/changeRequests'
import type { ChangeRequestSubtask, SubtaskStatusOption, SubtaskTypeOption, UserSelect, ChangeRequestPriority } from '@/models/changeRequest'
import { uniqBy } from 'lodash'

interface IProps {
  modelValue?: boolean,
  changeRequestSubtaskId?: number | string | undefined,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  changeRequestSubtaskId: undefined,
})

interface Emit {
  (e: 'update:modelValue', value: boolean): void
  (e: 'onClose'): void
  (e: 'onUpdateSubtask'): void
}

const emit = defineEmits<Emit>()

const route = useRoute()

const snackbarStore = useSnackbarStore()

const showSelectUsersDialog = ref<boolean>(false)

const changeRequestSubtask = ref<ChangeRequestSubtask | null>(null)

const subtaskStatusOptions = ref<SubtaskStatusOption[]>([])
const subtaskTypeOptions = ref<SubtaskTypeOption[]>([])
const subtaskPriorityOptions = ref<ChangeRequestPriority[]>([])

const editSubtaskId = ref<number | null>(null)

const loading = ref<boolean>(false)
let recordLoadVersion = 0

const getChangeRequestSubtask = async () => {
  const loadVersion = ++recordLoadVersion
  const recordId = props.changeRequestSubtaskId
  changeRequestSubtask.value = null
  editSubtaskId.value = null
  if (!props.changeRequestSubtaskId) return

  loading.value = true

  try {
    const res = await $getChangeRequestSubtask(props.changeRequestSubtaskId)
    if (loadVersion !== recordLoadVersion || recordId !== props.changeRequestSubtaskId || !props.modelValue) return
    changeRequestSubtask.value = res.data
  } catch (e: any) {
    console.log(e)
  } finally {
    if (loadVersion === recordLoadVersion) loading.value = false
  }
}

const getChangeRequestSubtaskOptions = async () => {
  try {
    const res = await $getChangeRequestSubtaskOptions()
    subtaskStatusOptions.value = res.data.actions.POST.status.choices
    subtaskTypeOptions.value = res.data.actions.POST.subtask_type.choices
    subtaskPriorityOptions.value = res.data.actions.POST.priority.choices
  } catch (e: any) {
    console.log(e)
  }
}

const updateChangeRequestSubtask = async () => {
  if (changeRequestSubtask.value?.has_ordinary_access !== true) return
  if (!changeRequestSubtask.value || !changeRequestSubtask.value.id) return

  loading.value = true

  const payload = {
    description: changeRequestSubtask.value.description ? changeRequestSubtask.value.description : '',
    subtask_type: changeRequestSubtask.value.subtask_type ? changeRequestSubtask.value.subtask_type : undefined,
    status: changeRequestSubtask.value.status ? changeRequestSubtask.value.status : undefined,
    priority: changeRequestSubtask.value.priority !== undefined ? changeRequestSubtask.value.priority : null,
    due_date: changeRequestSubtask.value.due_date ? changeRequestSubtask.value.due_date : null,
    supplier: changeRequestSubtask.value?.supplier_info?.id ? changeRequestSubtask.value.supplier_info.id : null,
    supplier_cost: changeRequestSubtask.value?.supplier_cost ? changeRequestSubtask.value?.supplier_cost : 0,
  } as ChangeRequestSubtask

  const formData = new FormData()

  if (Array.isArray(changeRequestSubtask.value?.assigned_to_users_info)) {
    changeRequestSubtask.value?.assigned_to_users_info.map(i => i.id).forEach((item: any, index) => {
      formData.append('assigned_to_users', item)
    })
  }

  if (changeRequestSubtask.value.files && changeRequestSubtask.value.files.length) {
    for (let i = 0; i < changeRequestSubtask.value.files.length; i++) {
      formData.append('files', changeRequestSubtask.value.files[i])
    }
  }

  if (changeRequestSubtask.value.delete_files && changeRequestSubtask.value.delete_files.length) {
    for (let i = 0; i < changeRequestSubtask.value.delete_files.length; i++) {
      formData.append('delete_files', changeRequestSubtask.value.delete_files[i])
    }
  }

  // Add ChangeRequestSubtask fields to FormData
  for (const key in payload) {
    const value = payload[key as keyof ChangeRequestSubtask]
    if (key === 'due_date') {
      formData.append('due_date', value ? value as string : '')
    }
    else if (value !== null) {
      // If services_required
      if (Array.isArray(value)) {
        value.forEach((item: any, index) => {
          formData.append(`${key}`, item)
        })
      } else {
        formData.append(key, value as string)
      }
    }
  }

  try {
    await $updateChangeRequestSubtask(String(changeRequestSubtask.value.id), formData)

    changeRequestSubtask.value = null
    editSubtaskId.value = null

    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Subtask updated successfully!'
    })
    await getChangeRequestSubtask()

    emit('onUpdateSubtask')
  } catch (e: any) {
    console.log(e)
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: 'Oops, something went wrong, try again'
    })
  } finally {
    loading.value = false
  }
}

const onRequestSubtaskSelectUsers = (users: UserSelect[]) => {
  if (!changeRequestSubtask.value) return
  const currentAssignedToUsers = changeRequestSubtask.value?.assigned_to_users_info?.length ?
    changeRequestSubtask.value.assigned_to_users_info : []
  // Set uniq users
  changeRequestSubtask.value.assigned_to_users_info = uniqBy([ ...currentAssignedToUsers, ...users], 'id')
}

const hideDialog = () => {
  emit('update:modelValue', false)
  onClose()
}

const onClose = async () => {
  editSubtaskId.value = null
  emit('onClose')
}

watch(() => [props.modelValue, props.changeRequestSubtaskId], () => {
  ++recordLoadVersion
  changeRequestSubtask.value = null
  editSubtaskId.value = null
  // on show Dialog
  if (!!props.modelValue) {
    getChangeRequestSubtask()
    getChangeRequestSubtaskOptions()
  }
  // on hide Dialog
  else {
    onClose()
  }
})
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="800"
    :persistent="!!loading"
    @after-leave="onClose"
    @update:modelValue="emit('update:modelValue')"
  >
    <VBtn
      variant="elevated"
      density="compact"
      icon="tabler-x"
      :disabled="!!loading"
      class="v-dialog-close-btn"
      @click="hideDialog"
    />

    <VCard>
      <VCardText>
        <VOverlay
          v-model="loading"
          persistent
          absolute
          contained
          class="align-center justify-center"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </VOverlay>

        <SelectUsersDialog
          v-model="showSelectUsersDialog"
          :dialog-title="'Assigned To Users'"
          :user-select-label="'Assigned To'"
          @on-submit="users => onRequestSubtaskSelectUsers(users)"
        />

        <ChangeRequestSubtaskInfo
          v-model:show-select-users-dialog="showSelectUsersDialog"
          :change-request-loading="loading"
          :subtask-status-options="subtaskStatusOptions"
          :subtask-type-options="subtaskTypeOptions"
          :subtask-priority-options="subtaskPriorityOptions"
          :request-subtask="changeRequestSubtask"
          :edit-subtask-id="editSubtaskId"
          :hide-delete-btn="true"
          :hide-duplicate-btn="true"
          @update:edit-subtask-id="(val) => { editSubtaskId = val }"
          @update:request-subtask="(val) => !!editSubtaskId && val ? changeRequestSubtask = val : ''"
          @onUpdateChangeRequestSubtask="updateChangeRequestSubtask()"
          @onAddChangeRequestSubtaskComment="getChangeRequestSubtask()"
        />
      </VCardText>
    </VCard>
  </VDialog>
</template>
